/**
 * Generates actionable insights from existing financial data.
 * All computation is client-side using data already fetched by React Query.
 *
 * @param {Object} params
 * @param {Object} params.statsData - from useStatsQuery (category breakdown with budgets)
 * @param {Object} params.summary - from useSummaryQuery (totalIncome, totalExpense)
 * @param {Array} params.recurringTransactions - from useRecurringTransactionsQuery
 * @param {Function} params.formatAmount - currency formatter
 * @returns {Array<{type: string, title: string, description: string, action?: {label: string, path: string}, priority: number}>}
 */
export function generateInsights({ statsData, summary, recurringTransactions = [], formatAmount }) {
  const insights = [];
  const fmt = formatAmount || ((v) => `₹${Math.round(v).toLocaleString('en-IN')}`);

  const totalIncome = summary?.totalIncome || 0;
  const totalExpense = summary?.totalExpense || 0;

  // 1. Over-budget categories
  if (statsData?.categoryBreakdown?.expense) {
    Object.entries(statsData.categoryBreakdown.expense).forEach(([category, data]) => {
      if (data.budget && data.budget > 0) {
        const overBy = data.total - data.budget;
        if (overBy > 0) {
          insights.push({
            type: 'warning',
            title: `${category} is over budget`,
            description: `Try to cut back on ${category}, you're ${fmt(overBy)} over your ${fmt(data.budget)} limit.`,
            action: { label: 'View Stats', path: '/stats?type=expense' },
            priority: 5
          });
        }
      }
    });
  }

  // 2. Expense > Income
  if (totalIncome > 0 && totalExpense > totalIncome) {
    const gap = totalExpense - totalIncome;
    insights.push({
      type: 'warning',
      title: 'Spending exceeds income',
      description: `You're spending more than you earn this month. Gap: ${fmt(gap)}.`,
      action: { label: 'View Insights', path: '/insights' },
      priority: 9
    });
  }

  // 3. Subscription cost summary
  const subscriptions = (recurringTransactions || []).filter(t => t.isSubscription && t.isActive);
  if (subscriptions.length > 0) {
    const monthlyCost = subscriptions.reduce((sum, t) => {
      return sum + normalizeToMonthly(t.amount || 0, t.frequency);
    }, 0);
    const yearlyCost = monthlyCost * 12;

    insights.push({
      type: 'tip',
      title: `${subscriptions.length} active subscription${subscriptions.length > 1 ? 's' : ''}`,
      description: `You're spending ${fmt(Math.round(monthlyCost))}/month on subscriptions (${fmt(Math.round(yearlyCost))}/year).`,
      action: { label: 'View Subscriptions', path: '/recurring' },
      priority: 5
    });
  }

  // 4. High total paid on a subscription
  const allSubs = (recurringTransactions || []).filter(t => t.isSubscription);
  allSubs.forEach(sub => {
    const totalPaid = (sub.amount || 0) * (sub.executionCount || 0);
    if (totalPaid > 0 && sub.executionCount >= 3) {
      insights.push({
        type: 'tip',
        title: `${fmt(Math.round(totalPaid))} paid to ${sub.description || 'a subscription'}`,
        description: `You've paid ${fmt(Math.round(totalPaid))} over ${sub.executionCount} payments. Still worth it?`,
        action: { label: 'Review', path: '/recurring' },
        priority: 3
      });
    }
  });

  // 5. Savings rate insights — only show after 15th of the month and
  //    when there are enough expense transactions to be meaningful
  const dayOfMonth = new Date().getDate();
  const expenseTransactionCount = statsData?.summary?.transactionCount || 0;
  const hasSufficientData = dayOfMonth >= 15 && expenseTransactionCount >= 10;

  if (totalIncome > 0 && hasSufficientData) {
    const savings = totalIncome - totalExpense;
    const savingsRate = Math.round((savings / totalIncome) * 100);

    if (savingsRate >= 20) {
      insights.push({
        type: 'success',
        title: `Saving ${savingsRate}% of income`,
        description: `You're saving ${savingsRate}% of your income, keep it up!`,
        priority: 4
      });
    } else if (savingsRate >= 0 && savingsRate < 20) {
      // 6. Low savings rate
      insights.push({
        type: 'tip',
        title: 'You can save more',
        description: `You've saved ${savingsRate}% this month. Small cutbacks could help you reach 20%.`,
        action: { label: 'View Insights', path: '/insights' },
        priority: 6
      });
    }
  }

  // Sort by priority descending
  return insights.sort((a, b) => b.priority - a.priority);
}

function normalizeToMonthly(amount, frequency) {
  switch (frequency) {
    case 'weekly': return amount * (52 / 12);
    case 'monthly': return amount;
    case 'quarterly': return amount / 3;
    case 'yearly': return amount / 12;
    default: return amount;
  }
}
